/**
 * Admin -- Notifications/Escalation (Wave 1b W1b-26)
 *
 * Real backend wiring for n-step escalation policies. Replaces the
 * mock-data-driven UI from Round 11.
 *
 * Uses TanStack Query for reads + invalidation. References the
 * /admin/notifications/escalation/* surface backed by
 * routes/v1/admin/notifications/escalation.ts.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  GitBranch, Plus, Pencil, Trash2, MoreHorizontal, ArrowDown,
  CheckCircle2, AlertTriangle, X as XIcon, Clock, RefreshCw,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { SectionCard } from '@/components/admin';
import { TypedConfirmDialog } from '@/components/admin/typed-confirm-dialog';
import { apiClient } from '@/lib/api-client';
import { queryClient } from '@/lib/query-client';
import { relativeTime } from '@/lib/format';

export const Route = createFileRoute('/admin/notifications/escalation')({
  component: EscalationPage,
});

// ─── Types (mirror backend response shapes) ─────────────────────────────────

interface PolicyListItem {
  id: string;
  name: string;
  description: string | null;
  triggerEventPattern: string;
  isActive: boolean;
  stepCount: number;
  createdAt: string;
  updatedAt: string;
}

interface PolicyStep {
  id?: string;
  stepOrder: number;
  delaySeconds: number;
  channelIds: string[];
  ackRequired: boolean;
}

interface PolicyDetail {
  id: string;
  name: string;
  description: string | null;
  triggerEventPattern: string;
  isActive: boolean;
  steps: PolicyStep[];
  createdAt: string;
  updatedAt: string;
}

interface InvocationListItem {
  id: string;
  policyId: string;
  policyName: string | null;
  triggeringEventName: string | null;
  currentStep: number;
  status: string;
  startedAt: string;
  acknowledgedAt: string | null;
  acknowledgedVia: string | null;
  completedAt: string | null;
}

interface ChannelListItem {
  id: string;
  name: string;
  channelType: string;
  isEnabled: boolean;
}

// ─── Page ───────────────────────────────────────────────────────────────────

function EscalationPage() {
  const [editing, setEditing] = useState<PolicyDetail | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<PolicyListItem | null>(null);
  const [selectedPolicyId, setSelectedPolicyId] = useState<string | null>(null);

  const policiesQuery = useQuery<{ policies: PolicyListItem[]; total: number }>({
    queryKey: ['admin', 'notifications', 'escalation', 'policies'],
    queryFn: () =>
      apiClient.get('/admin/notifications/escalation/policies?limit=200'),
  });

  const channelsQuery = useQuery<{ channels: ChannelListItem[] }>({
    queryKey: ['admin', 'notifications', 'channels'],
    queryFn: () => apiClient.get('/admin/notifications/channels'),
  });

  const invocationsQuery = useQuery<{
    invocations: InvocationListItem[];
    total: number;
  }>({
    queryKey: ['admin', 'notifications', 'escalation', 'invocations'],
    queryFn: () =>
      apiClient.get(
        '/admin/notifications/escalation/invocations?limit=25'
      ),
    refetchInterval: 15_000,
  });

  const detailQuery = useQuery<PolicyDetail>({
    queryKey: [
      'admin',
      'notifications',
      'escalation',
      'policies',
      selectedPolicyId,
    ],
    queryFn: () =>
      apiClient.get(
        `/admin/notifications/escalation/policies/${selectedPolicyId}`
      ),
    enabled: !!selectedPolicyId,
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      apiClient.patch(
        `/admin/notifications/escalation/policies/${id}`,
        { isActive }
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['admin', 'notifications', 'escalation', 'policies'],
      });
      toast.success('Policy updated');
    },
    onError: (err) => toast.error(`Toggle failed: ${(err as Error).message}`),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      apiClient.delete(`/admin/notifications/escalation/policies/${id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['admin', 'notifications', 'escalation', 'policies'],
      });
      toast.success('Escalation policy deleted');
      setDeleting(null);
    },
    onError: (err) => toast.error(`Delete failed: ${(err as Error).message}`),
  });

  const ackMutation = useMutation({
    mutationFn: (id: string) =>
      apiClient.post(
        `/admin/notifications/escalation/invocations/${id}/ack`
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['admin', 'notifications', 'escalation', 'invocations'],
      });
      toast.success('Invocation acknowledged');
    },
    onError: (err) =>
      toast.error(`Acknowledgment failed: ${(err as Error).message}`),
  });

  const cancelMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      apiClient.post(
        `/admin/notifications/escalation/invocations/${id}/cancel`,
        { reason }
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['admin', 'notifications', 'escalation', 'invocations'],
      });
      toast.success('Invocation cancelled');
    },
    onError: (err) =>
      toast.error(`Cancel failed: ${(err as Error).message}`),
  });

  const policies = policiesQuery.data?.policies ?? [];
  const channels = channelsQuery.data?.channels ?? [];
  const invocations = invocationsQuery.data?.invocations ?? [];

  return (
    <div
      className="max-w-6xl mx-auto p-6 space-y-5"
      data-testid="escalation-page"
    >
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold">Escalation Policies</h1>
          <p className="text-xs text-muted-foreground mt-1">
            For unacked critical events, escalate through steps until someone
            responds. Standard PagerDuty-style.
          </p>
        </div>
        <Button
          size="sm"
          className="h-8 text-xs gap-1.5"
          onClick={() => setCreating(true)}
          data-testid="new-policy-button"
        >
          <Plus className="h-3.5 w-3.5" />
          New policy
        </Button>
      </div>

      {policiesQuery.isLoading ? (
        <div className="text-sm text-muted-foreground">Loading policies...</div>
      ) : policies.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <GitBranch className="h-8 w-8 text-muted-foreground/50 mb-2" />
            <div className="text-xs font-medium">No escalation policies</div>
            <p className="text-[10px] text-muted-foreground mt-1 max-w-xs">
              Without escalation, unacked alerts stay with the initial recipient.
              Create a policy to fan out to backup responders after a delay.
            </p>
            <Button
              size="sm"
              className="h-8 text-xs gap-1.5 mt-3"
              onClick={() => setCreating(true)}
            >
              <Plus className="h-3.5 w-3.5" />
              Create first policy
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {policies.map((p) => (
            <PolicyCard
              key={p.id}
              policy={p}
              isSelected={p.id === selectedPolicyId}
              onSelect={() =>
                setSelectedPolicyId(p.id === selectedPolicyId ? null : p.id)
              }
              onEdit={async () => {
                // Always fetch fresh detail before opening editor.
                try {
                  const detail = await apiClient.get<PolicyDetail>(
                    `/admin/notifications/escalation/policies/${p.id}`
                  );
                  setEditing(detail);
                } catch (err) {
                  toast.error(
                    `Failed to load policy: ${(err as Error).message}`
                  );
                }
              }}
              onToggle={(checked) =>
                toggleMutation.mutate({ id: p.id, isActive: checked })
              }
              onDelete={() => setDeleting(p)}
              channels={channels}
            />
          ))}
        </div>
      )}

      {/* Selected policy detail panel + invocations */}
      {selectedPolicyId && detailQuery.data && (
        <SectionCard
          title={`Steps: ${detailQuery.data.name}`}
          description="Read-only view of the saved steps. Use Edit to mutate."
        >
          <StepList steps={detailQuery.data.steps} channels={channels} />
        </SectionCard>
      )}

      {/* Invocations history */}
      <SectionCard
        title="Recent invocations"
        icon={Clock}
        description="Last 25 escalation invocations across all policies."
        actions={
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-[10px] gap-1.5"
            onClick={() => invocationsQuery.refetch()}
            disabled={invocationsQuery.isFetching}
            data-testid="refresh-invocations"
          >
            <RefreshCw
              className={cn(
                'h-3 w-3',
                invocationsQuery.isFetching && 'animate-spin'
              )}
            />
            Refresh
          </Button>
        }
      >
        {invocationsQuery.isLoading ? (
          <div className="text-xs text-muted-foreground">
            Loading invocations...
          </div>
        ) : invocations.length === 0 ? (
          <div className="text-xs text-muted-foreground italic">
            No invocations yet. Trigger one by firing a matching event from a
            rule that overlaps a policy's trigger pattern.
          </div>
        ) : (
          <div className="space-y-1">
            {invocations.map((inv) => (
              <InvocationRow
                key={inv.id}
                invocation={inv}
                onAck={() => ackMutation.mutate(inv.id)}
                onCancel={() => {
                  const reason = window.prompt('Cancellation reason?');
                  if (reason && reason.trim()) {
                    cancelMutation.mutate({
                      id: inv.id,
                      reason: reason.trim(),
                    });
                  }
                }}
              />
            ))}
          </div>
        )}
      </SectionCard>

      {/* Delete confirmation */}
      {deleting && (
        <TypedConfirmDialog
          open
          onOpenChange={(v) => !v && setDeleting(null)}
          title="Delete escalation policy"
          description={`Delete "${deleting.name}"? Active invocations will continue but no new ones will start.`}
          consequences={[
            'Future events matching the trigger pattern will not start an invocation',
            'In-flight invocations will complete normally',
            'Audit trail is preserved (soft delete)',
          ]}
          confirmPhrase={deleting.name}
          confirmLabel="Delete policy"
          variant="warning"
          requireReason
          onConfirm={() => deleteMutation.mutate(deleting.id)}
        />
      )}

      {editing && (
        <PolicyEditorSheet
          policy={editing}
          mode="edit"
          channels={channels}
          onClose={() => setEditing(null)}
        />
      )}

      {creating && (
        <PolicyEditorSheet
          policy={null}
          mode="create"
          channels={channels}
          onClose={() => setCreating(false)}
        />
      )}
    </div>
  );
}

// ─── PolicyCard ─────────────────────────────────────────────────────────────

function PolicyCard({
  policy,
  isSelected,
  onSelect,
  onEdit,
  onToggle,
  onDelete,
  channels,
}: {
  policy: PolicyListItem;
  isSelected: boolean;
  onSelect: () => void;
  onEdit: () => void;
  onToggle: (checked: boolean) => void;
  onDelete: () => void;
  channels: ChannelListItem[];
}) {
  return (
    <SectionCard
      title={policy.name}
      icon={GitBranch}
      description={
        policy.description ?? `Trigger: ${policy.triggerEventPattern}`
      }
      actions={
        <div className="flex items-center gap-2">
          <Switch
            checked={policy.isActive}
            onCheckedChange={onToggle}
            className="scale-75"
            aria-label={policy.isActive ? 'Disable policy' : 'Enable policy'}
            data-testid={`policy-toggle-${policy.id}`}
          />
          <Badge
            variant={policy.isActive ? 'default' : 'outline'}
            className="h-5 text-[10px] px-1.5"
          >
            {policy.isActive ? 'Active' : 'Disabled'}
          </Badge>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                title="More actions"
                data-testid={`policy-menu-${policy.id}`}
              >
                <MoreHorizontal className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              <DropdownMenuItem
                onClick={onSelect}
                className="text-xs gap-2"
              >
                <GitBranch className="h-3 w-3" />
                {isSelected ? 'Hide steps' : 'View steps'}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onEdit} className="text-xs gap-2">
                <Pencil className="h-3 w-3" />
                Edit policy
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={onDelete}
                className="text-xs gap-2 text-red-600 focus:text-red-600"
              >
                <Trash2 className="h-3 w-3" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      }
    >
      <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
        <span>
          <span className="font-mono">{policy.triggerEventPattern}</span>
        </span>
        <span>·</span>
        <span>
          {policy.stepCount} step{policy.stepCount === 1 ? '' : 's'}
        </span>
        <span>·</span>
        <span title={`Updated ${policy.updatedAt}`}>
          updated {relativeTime(policy.updatedAt)}
        </span>
      </div>
    </SectionCard>
  );
}

// ─── StepList (read-only preview) ───────────────────────────────────────────

function StepList({
  steps,
  channels,
}: {
  steps: PolicyStep[];
  channels: ChannelListItem[];
}) {
  const channelById = useMemo(
    () => new Map(channels.map((c) => [c.id, c])),
    [channels]
  );
  return (
    <div className="space-y-1" data-testid="step-list">
      {steps.map((step, idx) => (
        <div key={step.id ?? idx}>
          <div className="border rounded-md p-3 flex items-center gap-3">
            <div className="h-7 w-7 rounded bg-primary/10 text-primary flex items-center justify-center shrink-0 text-[11px] font-bold tabular-nums">
              {step.stepOrder}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium">
                  {step.delaySeconds === 0
                    ? 'Immediate'
                    : `After ${formatSeconds(step.delaySeconds)}`}
                </span>
                {step.ackRequired && (
                  <Badge
                    variant="outline"
                    className="h-4 text-[9px] px-1.5 gap-1"
                  >
                    <CheckCircle2 className="h-2 w-2" />
                    Ack required
                  </Badge>
                )}
              </div>
              <div className="mt-1 flex flex-wrap gap-1">
                {step.channelIds.map((cid) => {
                  const ch = channelById.get(cid);
                  return (
                    <Badge
                      key={cid}
                      variant="secondary"
                      className="h-4 text-[9px] px-1.5"
                    >
                      {ch ? ch.name : `(missing ${cid.slice(0, 6)})`}
                    </Badge>
                  );
                })}
                {step.channelIds.length === 0 && (
                  <span className="text-[9px] text-muted-foreground italic">
                    No channels
                  </span>
                )}
              </div>
            </div>
          </div>
          {idx < steps.length - 1 && (
            <div className="flex items-center justify-center h-4 -my-0.5">
              <ArrowDown className="h-3 w-3 text-muted-foreground" />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── InvocationRow ──────────────────────────────────────────────────────────

function InvocationRow({
  invocation,
  onAck,
  onCancel,
}: {
  invocation: InvocationListItem;
  onAck: () => void;
  onCancel: () => void;
}) {
  const statusColor =
    invocation.status === 'active'
      ? 'text-amber-600 dark:text-amber-400'
      : invocation.status === 'acknowledged'
        ? 'text-emerald-600 dark:text-emerald-400'
        : invocation.status === 'cancelled'
          ? 'text-muted-foreground'
          : 'text-red-600 dark:text-red-400';

  return (
    <div
      className="border rounded-md p-3 flex items-center gap-3 text-xs"
      data-testid={`invocation-row-${invocation.id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium truncate">
            {invocation.policyName ?? '(deleted policy)'}
          </span>
          <Badge variant="outline" className={cn('h-4 text-[9px]', statusColor)}>
            {invocation.status}
          </Badge>
          <Badge variant="secondary" className="h-4 text-[9px]">
            step {invocation.currentStep}
          </Badge>
        </div>
        <div className="mt-1 text-[10px] text-muted-foreground">
          <span className="font-mono">
            {invocation.triggeringEventName ?? '(no event)'}
          </span>
          <span> · started {relativeTime(invocation.startedAt)}</span>
          {invocation.acknowledgedAt && (
            <span>
              {' '}· acked {relativeTime(invocation.acknowledgedAt)}
              {invocation.acknowledgedVia && ` (via ${invocation.acknowledgedVia})`}
            </span>
          )}
        </div>
      </div>
      {invocation.status === 'active' && (
        <div className="flex gap-1 shrink-0">
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-[10px]"
            onClick={onAck}
            data-testid={`ack-${invocation.id}`}
            title="Acknowledge invocation"
          >
            Ack
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-[10px] text-red-600"
            onClick={onCancel}
            data-testid={`cancel-${invocation.id}`}
            title="Cancel invocation"
          >
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── PolicyEditorSheet ──────────────────────────────────────────────────────

interface DraftPolicy {
  id?: string;
  name: string;
  description: string;
  triggerEventPattern: string;
  isActive: boolean;
  steps: PolicyStep[];
}

function PolicyEditorSheet({
  policy,
  mode,
  channels,
  onClose,
}: {
  policy: PolicyDetail | null;
  mode: 'edit' | 'create';
  channels: ChannelListItem[];
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<DraftPolicy>(() =>
    policy
      ? {
          id: policy.id,
          name: policy.name,
          description: policy.description ?? '',
          triggerEventPattern: policy.triggerEventPattern,
          isActive: policy.isActive,
          steps: policy.steps.map((s) => ({
            stepOrder: s.stepOrder,
            delaySeconds: s.delaySeconds,
            channelIds: [...s.channelIds],
            ackRequired: s.ackRequired,
          })),
        }
      : {
          name: '',
          description: '',
          triggerEventPattern: '',
          isActive: false,
          steps: [
            {
              stepOrder: 0,
              delaySeconds: 0,
              channelIds: [],
              ackRequired: true,
            },
          ],
        }
  );

  const createMutation = useMutation({
    mutationFn: () =>
      apiClient.post('/admin/notifications/escalation/policies', {
        name: draft.name.trim(),
        description: draft.description.trim() || null,
        triggerEventPattern: draft.triggerEventPattern.trim(),
        isActive: draft.isActive,
        steps: draft.steps,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['admin', 'notifications', 'escalation', 'policies'],
      });
      toast.success('Policy created');
      onClose();
    },
    onError: (err) =>
      toast.error(`Create failed: ${(err as Error).message}`),
  });

  const updateMetaMutation = useMutation({
    mutationFn: () =>
      apiClient.patch(
        `/admin/notifications/escalation/policies/${draft.id}`,
        {
          name: draft.name.trim(),
          description: draft.description.trim() || null,
          isActive: draft.isActive,
        }
      ),
  });

  const replaceStepsMutation = useMutation({
    mutationFn: () =>
      apiClient.put(
        `/admin/notifications/escalation/policies/${draft.id}/steps`,
        { steps: draft.steps }
      ),
  });

  function updateStep(idx: number, patch: Partial<PolicyStep>) {
    setDraft((prev) => ({
      ...prev,
      steps: prev.steps.map((s, i) => (i === idx ? { ...s, ...patch } : s)),
    }));
  }

  function toggleStepChannel(idx: number, channelId: string) {
    setDraft((prev) => {
      const step = prev.steps[idx];
      const next = step.channelIds.includes(channelId)
        ? step.channelIds.filter((c) => c !== channelId)
        : [...step.channelIds, channelId];
      return {
        ...prev,
        steps: prev.steps.map((s, i) =>
          i === idx ? { ...s, channelIds: next } : s
        ),
      };
    });
  }

  function addStep() {
    setDraft((prev) => {
      const lastDelay = prev.steps[prev.steps.length - 1]?.delaySeconds ?? 0;
      const nextOrder =
        prev.steps.length === 0
          ? 0
          : Math.max(...prev.steps.map((s) => s.stepOrder)) + 1;
      return {
        ...prev,
        steps: [
          ...prev.steps,
          {
            stepOrder: nextOrder,
            delaySeconds: lastDelay + 300,
            channelIds: [],
            ackRequired: true,
          },
        ],
      };
    });
  }

  function removeStep(idx: number) {
    if (draft.steps.length === 1) {
      toast.error('At least one step is required');
      return;
    }
    setDraft((prev) => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== idx),
    }));
  }

  function moveStep(idx: number, dir: -1 | 1) {
    const target = idx + dir;
    if (target < 0 || target >= draft.steps.length) return;
    setDraft((prev) => {
      const next = [...prev.steps];
      [next[idx], next[target]] = [next[target], next[idx]];
      // Reassign step_order to match new array index so the backend
      // accepts the put-steps body without DUPLICATE_STEP_ORDER.
      return {
        ...prev,
        steps: next.map((s, i) => ({ ...s, stepOrder: i })),
      };
    });
  }

  function validate(): string | null {
    if (!draft.name.trim()) return 'Policy name is required';
    if (!draft.triggerEventPattern.trim())
      return 'Trigger event pattern is required';
    if (draft.steps.length === 0) return 'Add at least one step';
    if (draft.steps.some((s) => s.channelIds.length === 0))
      return 'Every step needs at least one channel';
    return null;
  }

  async function save() {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    if (mode === 'create') {
      createMutation.mutate();
    } else {
      try {
        await updateMetaMutation.mutateAsync();
        await replaceStepsMutation.mutateAsync();
        await queryClient.invalidateQueries({
          queryKey: ['admin', 'notifications', 'escalation'],
        });
        toast.success('Policy updated');
        onClose();
      } catch (e) {
        toast.error(`Update failed: ${(e as Error).message}`);
      }
    }
  }

  return (
    <Sheet open onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        className="sm:max-w-xl p-0 flex flex-col"
        data-testid="policy-editor-sheet"
      >
        <SheetHeader className="p-5 border-b shrink-0">
          <SheetTitle className="text-base flex items-center gap-2">
            {mode === 'create' ? (
              <Plus className="h-4 w-4" />
            ) : (
              <Pencil className="h-4 w-4" />
            )}
            {mode === 'create' ? 'New escalation policy' : `Edit ${policy?.name}`}
          </SheetTitle>
          <SheetDescription className="text-xs">
            Steps fire in order. Each step's delay counts from the initial
            event -- not cumulatively.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto min-h-0 p-5 space-y-5">
          <div>
            <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">
              Policy name
            </Label>
            <Input
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder="e.g. Critical security default"
              className="h-8 text-xs mt-1"
              data-testid="policy-name-input"
            />
          </div>

          <div>
            <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">
              Trigger event pattern
            </Label>
            <Input
              value={draft.triggerEventPattern}
              onChange={(e) =>
                setDraft({ ...draft, triggerEventPattern: e.target.value })
              }
              placeholder="e.g. security.incident.* or audit.chain.broken"
              className="h-8 text-xs mt-1 font-mono"
              data-testid="policy-trigger-input"
              disabled={mode === 'edit'}
              title={
                mode === 'edit'
                  ? 'Trigger pattern is immutable after creation; create a new policy to change it.'
                  : undefined
              }
            />
          </div>

          <div>
            <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">
              Description
            </Label>
            <Textarea
              value={draft.description}
              onChange={(e) =>
                setDraft({ ...draft, description: e.target.value })
              }
              placeholder="When and why this policy applies"
              className="mt-1 text-[11px] min-h-[50px]"
            />
          </div>

          <div className="flex items-center gap-2">
            <Switch
              checked={draft.isActive}
              onCheckedChange={(v) => setDraft({ ...draft, isActive: v })}
              className="scale-75"
              data-testid="policy-active-switch"
            />
            <Label className="text-xs">Active</Label>
            <span className="text-[10px] text-muted-foreground">
              Inactive policies do not start invocations.
            </span>
          </div>

          <Separator />

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs font-semibold">Escalation steps</Label>
              <Button
                variant="outline"
                size="sm"
                className="h-6 text-[10px] gap-1 px-2"
                onClick={addStep}
                data-testid="add-step-button"
              >
                <Plus className="h-2.5 w-2.5" />
                Add step
              </Button>
            </div>

            <div className="space-y-2">
              {draft.steps.map((step, idx) => (
                <div
                  key={idx}
                  className="border rounded-md p-3 space-y-2"
                  data-testid={`step-editor-${idx}`}
                >
                  <div className="flex items-center gap-2">
                    <div className="h-6 w-6 rounded bg-primary/10 text-primary flex items-center justify-center shrink-0 text-[10px] font-bold">
                      {idx + 1}
                    </div>
                    <div className="flex-1 flex items-center gap-2 flex-wrap">
                      <Label className="text-[10px] text-muted-foreground whitespace-nowrap">
                        Delay
                      </Label>
                      <Input
                        type="number"
                        min={0}
                        value={step.delaySeconds}
                        onChange={(e) =>
                          updateStep(idx, {
                            delaySeconds: Math.max(
                              0,
                              parseInt(e.target.value, 10) || 0
                            ),
                          })
                        }
                        className="h-7 w-24 text-[11px] tabular-nums"
                      />
                      <span className="text-[10px] text-muted-foreground">
                        sec
                      </span>
                      <label className="flex items-center gap-1.5 text-[10px] cursor-pointer">
                        <Switch
                          checked={step.ackRequired}
                          onCheckedChange={(v) =>
                            updateStep(idx, { ackRequired: v })
                          }
                          className="scale-75"
                        />
                        Ack required
                      </label>
                    </div>
                    <div className="flex gap-0.5">
                      {/* C-A11Y cluster (issue #647 ADMIN-A11Y-002):
                          Arrow glyph alone was the only accessible name screen
                          readers exposed. Add aria-label + sr-only label and
                          keep the visual glyph aria-hidden. */}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => moveStep(idx, -1)}
                        disabled={idx === 0}
                        title="Move step up"
                        aria-label={`Move step ${idx + 1} up`}
                      >
                        <span aria-hidden="true">↑</span>
                        <span className="sr-only">Move step up</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => moveStep(idx, 1)}
                        disabled={idx === draft.steps.length - 1}
                        title="Move step down"
                        aria-label={`Move step ${idx + 1} down`}
                      >
                        <span aria-hidden="true">↓</span>
                        <span className="sr-only">Move step down</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => removeStep(idx)}
                        title="Remove step"
                        disabled={draft.steps.length === 1}
                      >
                        <XIcon className="h-3 w-3 text-red-500" />
                      </Button>
                    </div>
                  </div>

                  <div>
                    <div className="text-[10px] text-muted-foreground mb-1">
                      Channels
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {channels.length === 0 ? (
                        <span className="text-[10px] text-muted-foreground italic">
                          No channels available. Create one in the Channels
                          tab first.
                        </span>
                      ) : (
                        channels.map((c) => {
                          const selected = step.channelIds.includes(c.id);
                          return (
                            <button
                              key={c.id}
                              type="button"
                              onClick={() => toggleStepChannel(idx, c.id)}
                              className={cn(
                                'px-2 py-1 rounded border text-[10px] transition-colors',
                                selected
                                  ? 'border-primary bg-primary/10 text-primary'
                                  : 'hover:bg-muted/30',
                                !c.isEnabled && 'opacity-60'
                              )}
                              data-testid={`channel-pill-${idx}-${c.id}`}
                            >
                              {c.name}
                              {!c.isEnabled && (
                                <span className="ml-1 text-[9px]">
                                  (off)
                                </span>
                              )}
                            </button>
                          );
                        })
                      )}
                    </div>
                    {step.channelIds.length === 0 && (
                      <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-1 flex items-center gap-1">
                        <AlertTriangle className="h-2.5 w-2.5" />
                        Select at least one channel
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="border-t shrink-0 p-4 flex justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            className="h-8 text-xs"
            onClick={save}
            disabled={
              createMutation.isPending ||
              updateMetaMutation.isPending ||
              replaceStepsMutation.isPending
            }
            data-testid="save-policy-button"
          >
            {mode === 'create' ? 'Create policy' : 'Save changes'}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatSeconds(s: number): string {
  if (s < 60) return `${s} sec`;
  const minutes = Math.floor(s / 60);
  const remainder = s % 60;
  if (minutes < 60) {
    return remainder === 0
      ? `${minutes} min`
      : `${minutes}m ${remainder}s`;
  }
  const hours = Math.floor(minutes / 60);
  const remMin = minutes % 60;
  return remMin === 0 ? `${hours}h` : `${hours}h ${remMin}m`;
}
